import { Injectable, Injector } from '@angular/core';
import { idle, ILocationService, IPromise, ITimeoutService, IWindowService } from 'angular';
import { IStateService } from 'angular-ui-router';
import { Auth0DecodedHash, Auth0UserProfile, AuthOptions, AuthorizeOptions, LogoutOptions, WebAuth } from 'auth0-js';
import * as jwt_decode from 'jwt-decode';
import { includes } from 'lodash';
import { UtilitiesService } from 'Src/ng2/shared/services/utilities/utilities.service';
import { LocalStorageService } from 'Src/ng2/shared/services/web-storage/local-storage/local-storage.service';
import { PortalConfig } from '../services/portal-config';

/**
 * Interface of decoded Auth0 token (CM).
 */
interface IIdTokenDecoded {
  email: string;
  email_verified: boolean;
  iss: string;
  sub: string;
  aud: string;
  iat: number;
  exp: number;
}

/**
 * Interface of user returned by Auth0 login success rule (CM).
 */
export interface IAuth0Profile extends Auth0UserProfile {
  dbUser: IDbUser;
}

interface IDbUser {
  _id: string;
}

export type TAuthLocalStorageVals =
  | 'idToken'
  | 'accessToken'
  | 'lastKnownUrl'
  | 'currentUserId'
  | 'errorMessage'
  | 'loginMethod'
  | 'ObjectCache';

@Injectable()
export class Auth {
  private auth0: WebAuth;
  private readonly AUTHORIZE_OPTIONS_GOOGLE: AuthorizeOptions = {
    connection: 'google-oauth2',
    prompt: 'select_account',
  };

  private readonly AUTHORIZE_OPTIONS_NYCDOE: AuthorizeOptions = {
    connection: 'NYCDOE',
  };

  // These are error messages that auth0 can return when renewing a token.
  // No patterns have been identified as to when these errors are returned by auth0,
  // so we ignore them and attempt to renew the token again (CM).
  private readonly AUTH0_ERR_DESCRIPTIONS_TO_IGNORE: string[] = [
    'Algorithm HS256 is not supported. (Expected algs: [RS256])',
    'Timeout during executing web_message communication',
    'Login required',
  ];

  private tokenRenewalTimeout: IPromise<void>;
  private publicConfig: PortalConfig['publicConfig'];

  constructor(
    private injector: Injector,
    private utilitiesService: UtilitiesService,
    private localStorageService: LocalStorageService,
    private portalConfig: PortalConfig,
  ) {
    this.publicConfig = this.portalConfig.publicConfig;
    // We dymamically get the `callbackUrl` for the env;
    // We use `window` instead of `$window` because AngularJS services are not yet
    // available inside of config blocks (CM).
    const { hostname, port, protocol } = window.location;
    const callbackPath = '/callback';
    // window.location.origin would provide the same result, but it is not supported in IE (CM).
    const origin = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    let callbackUrl = `${origin}${callbackPath}`;

    // Initialization opts for auth0 (CM).
    const AUTH_OPTIONS: AuthOptions = {
      clientID: this.publicConfig.AUTH0_CLIENT_ID,
      domain: this.publicConfig.AUTH0_HOSTNAME,
      redirectUri: callbackUrl,
      responseType: 'token id_token',
      scope: 'openid profile email',
    };
    this.auth0 = new WebAuth(AUTH_OPTIONS);
  }

  /**
   * gets Auth0 `profile.dbUser._id`
   */
  public async getCurrentUserId(): Promise<string> {
    let currentUserId: string = this.getValInLocalStorage('currentUserId');

    if (!currentUserId) {
      let profile: IAuth0Profile;
      try {
        profile = await this.getProfile();
      } catch (err) {
        throw this.utilitiesService.handleAwaitErr(err);
      }

      if (profile.dbUser && profile.dbUser._id) {
        // legacy auth0 scope value (CM).
        // TODO: remove `profile.dbUser._id` once all auth0 envs have been updated with newest loginSuccess rule (CM).
        currentUserId = profile.dbUser._id;
      } else {
        const dbUser = this.getDbUserFromProfile(profile);
        currentUserId = dbUser._id;
      }

      this.setValInLocalStorage('currentUserId', currentUserId);
    }
    return currentUserId;
  }

  private getDbUserFromProfile(profile) {
    // get auth0 custom claims, which meets auth0 current scope requirements (CM).
    // TODO: When connecting local frontend to local backend, we still make use of Auth0 dev
    // because there is not currently an Auth0 env for localhost. So we must use the dev API url
    // to find the user credentials. We should find a better way to do this. Either setup a
    // localhost Auth0 env or use a different URL (JC).
    const { dbUser } = profile[this.publicConfig.AUTH0_NAMESPACE || this.publicConfig.NV_API_ORIGIN];

    return dbUser;
  }

  /**
   * Gets `idToken` from local storage
   */
  public getIdToken() {
    const key = 'idToken';
    const idToken = this.getValInLocalStorage(key);

    return idToken;
  }

  /**
   * Handles auth0 authentication response by:
   * - setting tokens in local storage.
   * - redirecting the user to `home` or `lastKnownUrl` if tokens are valid.
   * - login out user if authentication fails (CM).
   * - if user is already logged in, schedule initial token renewal.
   */
  public handleAuthentication(): void {
    const $state: IStateService = this.injector.get('$state');
    const $location: ILocationService = this.injector.get('$location');
    const Rollbar = this.injector.get('Rollbar');
    this.auth0.parseHash((err, authResult) => {
      if (authResult) {
        // User has logged in and successfully authenticated, so locally set the credentials aquired from Auth0 (CM).
        this.setSession(authResult);
        const lastKnownUrl = this.getValInLocalStorage('lastKnownUrl');
        const goToLastKnownUrl = lastKnownUrl && !lastKnownUrl.includes('login');
        if (goToLastKnownUrl) {
          this.removeValInLocalStorage('lastKnownUrl');
          return $location.url(lastKnownUrl);
        }

        return $state.go('main.home');
      } else if (this.isAuthenticated()) {
        // User is already authenticated, so schedule token renewal (CM).
        this.scheduleRenewal();
      } else if (err) {
        // Error while user attempted to log in (CM).
        const { error, errorDescription } = err;
        const errorMessage = errorDescription || error;
        Rollbar.debug('Auth#handleAuthentication', err);

        this.logout({ errorMessage });
      }
    });
  }

  /**
   * Checks whether the user is authenticated (CM).
   */
  public isAuthenticated(): boolean {
    const idToken = this.getIdToken();

    if (!idToken) return false; // missing required local storage values (CM).
    const authenticated = !this.isTokenExpired(idToken);

    return authenticated;
  }

  /**
   * Logs in user using `google-oauth2` (CM).
   */
  public loginWithGoogle(): void {
    const Rollbar = this.injector.get('Rollbar');
    try {
      // initiates auth0 authentication (CM).
      this.auth0.authorize(this.AUTHORIZE_OPTIONS_GOOGLE);
      this.setValInLocalStorage('loginMethod', this.AUTHORIZE_OPTIONS_GOOGLE.connection);
    } catch (err) {
      Rollbar.debug('Auth#login using google email: ', err);
    }
  }

  /**
   * Logs in user using SAML (CM).
   */
  public loginWithNycDoe(): void {
    const Rollbar = this.injector.get('Rollbar');
    try {
      // initiates auth0 authentication (CM).
      this.auth0.authorize(this.AUTHORIZE_OPTIONS_NYCDOE);
      this.setValInLocalStorage('loginMethod', this.AUTHORIZE_OPTIONS_NYCDOE.connection);
    } catch (err) {
      Rollbar.debug('Auth#login using NYC DOE email: ', err);
    }
  }

  /**
   * Logs in user using auth0 on top of cares system
   */
  public loginWithCares(): void {
    //  TODO - TEMP solution until we have cares auth flow set up
    this.loginWithGoogle();
  }

  /**
   * Logs out user by:
   * - clearing local storage of tokens.
   * - clearing scheduled token renewal.
   * - redirecting user to login (CM).
   *
   * If `errorMessage` is passed in, it will be displayed in login box when user is redirected to login page (CM).
   * If `lastKnowUrl` is passed in, the user will be redirected to that url next time they log in (CM).
   */
  public logout(opts?: { errorMessage?: string; lastKnownUrl?: string }): void {
    const $timeout: ITimeoutService = this.injector.get('$timeout');
    const idle: idle.IIdleService = this.injector.get('Idle');
    const errorMessage = opts ? opts.errorMessage : null;
    const tokenPathPattern = /#access_token=/gi;
    const lastKnownUrlIsValid = opts && opts.lastKnownUrl && !opts.lastKnownUrl.match(tokenPathPattern);
    const lastKnownUrl = lastKnownUrlIsValid ? opts.lastKnownUrl : null;
    const auth0LogoutOpts: LogoutOptions = {
      returnTo: this.getReturnToUrl(),
    };
    // set error message in local storage, which will be displayed in login page (CM).
    if (errorMessage) this.setValInLocalStorage('errorMessage', errorMessage);

    // Set last know url in local storate (CM).
    if (lastKnownUrl) this.setValInLocalStorage('lastKnownUrl', lastKnownUrl);

    // Cancel existing token renewal timeout (CM).
    $timeout.cancel(this.tokenRenewalTimeout);

    // Stop watching for user idleness (CM).
    idle.unwatch();

    // Clear local storage (CM).
    this.clearLocalStorageOfAuthVals();

    // Ends auth0 session and refreshes browser (CM).
    this.auth0.logout(auth0LogoutOpts);
  }

  // clears local storage of values used for authentication (CM)
  public clearLocalStorageOfAuthVals() {
    // Remove login method
    this.removeValInLocalStorage('loginMethod');

    // Remove tokens from localStorage (CM).
    this.removeValInLocalStorage('accessToken');
    this.removeValInLocalStorage('idToken');

    // Remove currentUserId
    this.removeValInLocalStorage('currentUserId');

    // Remove ObjectCache
    this.removeValInLocalStorage('ObjectCache');
  }

  /**
   * Decodes a jwt token
   */
  private decodeToken(idToken: string): IIdTokenDecoded {
    let idTokenDecoded: IIdTokenDecoded;
    const Rollbar = this.injector.get('Rollbar');
    try {
      idTokenDecoded = jwt_decode<IIdTokenDecoded>(idToken);
    } catch (ex) {
      Rollbar.debug(`Auth#decodeToken`, ex);
      idTokenDecoded = null;
    }

    return idTokenDecoded;
  }

  /**
   * Fetches auth0 user
   */
  private getProfile(): Promise<IAuth0Profile> {
    const Rollbar = this.injector.get('Rollbar');
    const promise = new Promise<IAuth0Profile>((resolve, reject) => {
      const accessToken = this.getValInLocalStorage('accessToken');
      if (!accessToken) {
        return reject('Access token must exist to fetch profile');
      }
      this.auth0.client.userInfo(accessToken, (err, profile: IAuth0Profile) => {
        if (err) {
          Rollbar.debug('Error in Auth#getProfile', err);
          return reject(err);
        }

        return resolve(profile);
      });
    });

    return promise;
  }

  /**
   * Dynamically returns url for env that auth0 will redirect to on logout.
   * KEEP THIS LOGIC IN SYNC WITH how the auth0 `redirectTo` in
   * `auth.config.constant.ts` is generated. - unfortunately, we are not able
   * to inject services in config blocks; thus, cannot share this - method with
   * the login found in `auth.config.constant.ts`.
   */
  private getReturnToUrl(): string {
    const $window: IWindowService = this.injector.get('$window');
    // We dymamically get the `returnToUrl` for the env;
    const { hostname, port, protocol } = $window.location;
    const returnToPath = '/login';
    // window.location.origin would provide the same result, but it is not supported in IE (CM).
    const origin = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    let returnToUrl = `${origin}${returnToPath}`;

    return returnToUrl;
  }

  /**
   * Gets `exp` in seconds of a jwt token
   * If token does not have an `exp` property, it returns `null`. This can happen for tokens that do not expire (CM).
   */
  private getTokenExpInSeconds(idToken: string): number {
    const decodedToken = this.decodeToken(idToken);
    if (decodedToken) {
      const { exp } = decodedToken;

      if (exp) {
        const idTokenExpInSeconds = exp * 1000;

        return idTokenExpInSeconds;
      }
    }

    return null;
  }

  /**
   * Gets a value in local storage
   */
  private getValInLocalStorage(key: TAuthLocalStorageVals) {
    const val = this.localStorageService.getItem(key);

    return val;
  }

  /**
   * Checks whether a jwt token is expired
   */
  private isTokenExpired(idToken: string): boolean {
    const idTokenExpInSeconds = this.getTokenExpInSeconds(idToken);
    const now = new Date().getTime();
    // if `idTokenExpInSeconds` is `null`, then the token does not have expiration (CM).
    const expired = idTokenExpInSeconds !== null ? idTokenExpInSeconds < now : false;

    return expired;
  }

  /**
   * Removes a value in local storage
   */
  private removeValInLocalStorage(key: TAuthLocalStorageVals): void {
    this.localStorageService.removeItem(key);
  }

  /**
   * Renews auth0 token
   */
  private renewToken(): void {
    const $timeout: ITimeoutService = this.injector.get('$timeout');
    const Rollbar = this.injector.get('Rollbar');
    this.auth0.checkSession({}, (err, result) => {
      const _error = err || (result && result.error);
      if (_error) {
        // auth0 are inconsistent - some have an `errorDescription` property, other have `error_description` (CM).
        const errorDescription = _error.errorDescription || _error.error_description;
        const ignoreErrorDescription = includes(this.AUTH0_ERR_DESCRIPTIONS_TO_IGNORE, errorDescription);
        if (ignoreErrorDescription) {
          Rollbar.debug('Auth#renewToken - renewing token', _error);

          // Attempt to renew token again.
          // $timeout is to prevent infinite loop (CM).
          $timeout(() => {
            if (this.isAuthenticated()) {
              this.renewToken();
            } else {
              this.logout();
            }
          }, 5000);
        } else {
          Rollbar.debug('Auth#renewToken - logging out', _error);
          this.logout();
        }
      } else {
        this.setSession(result);
      }
    });
  }

  /**
   * Schedules an auth0 token renewal
   */
  private scheduleRenewal(idToken?: string): void {
    const $timeout: ITimeoutService = this.injector.get('$timeout');
    const _idToken = idToken || this.getIdToken();
    const idTokenExpInSeconds = this.getTokenExpInSeconds(_idToken);

    // if `idTokenExpInSeconds` is `null`, then the token does not have expiration (CM).
    if (idTokenExpInSeconds !== null) {
      let delay = idTokenExpInSeconds - Date.now();

      if (delay > 0) {
        // This sets the token renewal to happen in the amount of time remaining for the token to
        // expire minus 120 seconds (CM).
        delay -= 120000; // if this number is negative, it will immediately trigger the renewal (CM).

        this.tokenRenewalTimeout = $timeout(() => {
          this.renewToken();
        }, delay);
      }
    }
  }

  /**
   * Sets `accessToken` and `idToken` in local storage.
   * - `accessToken`: token used to get user profile from auth0.
   * - `idToken`: token used to get data from API.
   * Schedules token renewal (CM).
   */
  private setSession(authResult: Auth0DecodedHash): void {
    const { accessToken, idToken, idTokenPayload: profile } = authResult;
    const dbUser = this.getDbUserFromProfile(profile);
    const currentUserId = dbUser._id;

    this.setValInLocalStorage('currentUserId', currentUserId);
    this.setValInLocalStorage('accessToken', accessToken);
    this.setValInLocalStorage('idToken', idToken);
    this.scheduleRenewal(idToken);
  }

  /**
   * Sets a value in local storage
   */
  private setValInLocalStorage(key: TAuthLocalStorageVals, val: string) {
    this.localStorageService.setItem(key, val);
  }
}
